import { Box, Stack } from '@mui/material';
import { AzureOnboardingStatusStatusEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import type { AzureOwnCloudState } from './azureOwnCloudState';

import { FormTextField } from '../../../../../../components/form/formTextField';
import { BulletItem } from '../../bulletItem';
import { extractErrorMessage } from '../../errorHandling';
import { OwnCloudErrorDialog } from '../../ownCloudErrorDialog';

export const ConnectAzureOwnVaultAccount = (
  props: StepProps<AzureOwnCloudState>
) => {
  const [error, setError] = useState<Error | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const dal = useDAL();

  const code = `\
module "azure_vault" {
  source = "https://eon.blob.core.windows.net/public/onboarding/v1.1.2/terraform/vault.zip"

  subscription_id           = "${props.wizardState.vaultSubscriptionId ?? ''}"
  management_app_id         = "${props.wizardState.managementApplicationId ?? ''}"
  backup_app_id             = "${props.wizardState.backupAppId ?? ''}"
  adx_service_principal_id  = "${props.wizardState.adxClusterIdentity ?? ''}"
}`;

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);
    try {
      const response = await dal.cloudAccounts.vault.connectAzure({
        tenantId: props.wizardState.tenantId,
        vaultSubscriptionId: props.wizardState.vaultSubscriptionId!,
      });

      // Check if the response has a status field and it's not succeeded
      if (
        response &&
        response.status !== AzureOnboardingStatusStatusEnum.Succeeded
      ) {
        console.log(response);
        setError(new Error('Failed to deploy vault account'));
      } else {
        setIsDeploying(false);
        props.setNextStep(props.currentStep.next?.[0]);
      }
    } catch (error) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
      setIsDeploying(false);
    }
  };

  return (
    <>
      <StepContainer
        stepperIndex={2}
        stepperLabels={props.stepperLabels}
        onBackClick={props.back}
        canGoNext={() =>
          !isDeploying && !!props.wizardState.vaultSubscriptionId
        }
        nextButtonText={'Connect & Continue'}
        sx={{ overflow: 'auto' }}
        onNextClick={onNextClick}
        nextButtonDisabledTooltip={
          isDeploying ? 'Please wait for the deployment to complete' : undefined
        }
      >
        <Box paddingX='min(100px,6vw)' width='100%' height='auto'>
          <BulletItem
            title='Fill in the details to generate your Terraform module, then review and deploy it in your environment.'
            leftContent={
              <Stack gap='24px'>
                <FormTextField
                  title='Subscription ID'
                  placeholder='Example: 12345678-90ab-cdef-1234-567890abcdef'
                  value={props.wizardState.vaultSubscriptionId || ''}
                  onChange={(event) =>
                    props.setWizardState({
                      ...props.wizardState,
                      vaultSubscriptionId: event.target.value,
                    })
                  }
                />
              </Stack>
            }
            rightContent={<CopyableCode title='main.tf' code={code} />}
          />
        </Box>
      </StepContainer>
      <OwnCloudErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
